/**
 * Diskriminanten und Fehlertyp für die API-Clients (AFO-009 / DEV-015).
 *
 * Mapping in den Submit-Flow:
 * - `SERVICE_UNAVAILABLE`: Backend antwortet mit HTTP 503 (Killswitch / Wartung,
 *   AFO-003) → `TECHNICAL_ERROR` Variante `generic` (B-Variante, keine
 *   Pinning-Aussage), IDB-Cleanup.
 * - `RATE_LIMITED`: Backend antwortet mit HTTP 429 → `TECHNICAL_ERROR`
 *   Variante `generic`, IDB-Cleanup.
 * - `NOT_FOUND`: Backend antwortet mit HTTP 404 (z. B. unbekannte `jobId` im
 *   Status-Polling) → DEV-017-Polling bricht ab und meldet `TECHNICAL_ERROR`
 *   Variante `generic`.
 * - `NETWORK`: Fetch-Promise selbst wirft (DNS, Offline, TLS) → `TECHNICAL_ERROR`
 *   Variante `generic`, IDB-Cleanup.
 * - `SCHEMA_INVALID`: Response hat unerwartetes Format (kein JSON, falsche
 *   Felder, Längen-Mismatch) → `TECHNICAL_ERROR` Variante `generic`,
 *   IDB-Cleanup.
 *
 * Wir mappen bewusst alle Kinds auf die generische `TECHNICAL_ERROR`-Variante,
 * weil aus Patient:innen-Sicht jede dieser Ursachen die gleiche Handlung
 * verlangt: „Bitte rufen Sie die Praxis an." Pinning-spezifische Details
 * laufen über `PINNING_FAILED`, nicht über diese Klasse.
 */
export const ApiErrorKind = {
  ServiceUnavailable: 'SERVICE_UNAVAILABLE',
  RateLimited: 'RATE_LIMITED',
  NotFound: 'NOT_FOUND',
  Conflict: 'CONFLICT',
  PayloadTooLarge: 'PAYLOAD_TOO_LARGE',
  Network: 'NETWORK',
  SchemaInvalid: 'SCHEMA_INVALID',
} as const;

export type ApiErrorKind = (typeof ApiErrorKind)[keyof typeof ApiErrorKind];

interface ApiErrorOptions {
  readonly kind: ApiErrorKind;
  readonly cause?: unknown;
  /** Sekunden aus dem `Retry-After`-Header (nur bei `RATE_LIMITED`, T-9). */
  readonly retryAfterSeconds?: number;
}

/**
 * Typisierter Fehler der API-Clients.
 *
 * Aufrufer prüfen die Diskriminante via `error.kind` und ignorieren die
 * (englischsprachige) Message für UI-Texte — Microcopy liefert AFO-016.
 *
 * Konstruktor akzeptiert ein Options-Objekt (RO-RO), damit weitere Felder
 * (z. B. `statusCode`) später ohne Breaking Change ergänzt werden können.
 */
export class ApiError extends Error {
  public readonly kind: ApiErrorKind;
  public readonly retryAfterSeconds?: number;

  public constructor(options: ApiErrorOptions) {
    super(`ApiError: ${options.kind}`);
    this.name = 'ApiError';
    this.kind = options.kind;
    if (options.cause !== undefined) {
      this.cause = options.cause;
    }
    if (options.retryAfterSeconds !== undefined) {
      this.retryAfterSeconds = options.retryAfterSeconds;
    }
  }
}
